import { History } from 'history';
import { Component } from 'react';
import { useHistory } from 'react-router-dom';
import { GeneralErrorMessage } from '../GeneralErrorMessage';
import { logger } from '../../error-tracker';
import { noWhiteSpace } from '../../GlobalFunctions';
import * as styles from './ErrorBoundary.module.scss';

interface Props {
  history: History;
}

interface State {
  hasError: boolean;
  errorObject: Error | undefined;
}

export class InnerErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorObject: undefined,
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return {
      hasError: true,
      errorObject: error,
    };
  }

  hideDialog = () => this.setState({ hasError: false });

  componentDidCatch(error: Error) {
    // You can also log the error to an error reporting service
    logger.logException(error);
    console.error(`Error below was caught by the ErrorBoundary: ${error}`);
  }

  errorReload = (error: string, retries = 1) => {
    console.error(error);
    // Get the current URL
    const urlString = window.location.href;

    // Create a URL object
    const url = new URL(urlString);
    const errRetries = parseInt(url.searchParams.get('errRetries') || '0');
    if (errRetries >= retries) {
      window.history.replaceState(null, '', url.pathname);
      // useSnackbarStore().add(error, { reload: true })
      return;
    }

    // Update or add the query parameter
    url.searchParams.set('errRetries', String(errRetries + 1));

    // Reload the page with the updated URL
    window.location.href = url.toString();
    // window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (
        this.state.errorObject?.message.includes('Failed to fetch dynamically imported module') ||
        this.state.errorObject?.message.includes('Importing a module script failed')
      ) {
        this.errorReload('Failed to fetch dynamically imported module');
      }

      return (
        <GeneralErrorMessage
          isShow={this.state.hasError}
          onHide={this.hideDialog}
          messageText={noWhiteSpace`We’ve experienced a problem: ${this.state.errorObject?.message || 'unknown'}.\
            Please take a screenshot of this message and send it to Peekapak support.`}
          onOkay={() => this.props.history.push('/')}
          titleText='There’s an unexpected problem'
          titleClass={styles.errorTitle}
          dialogClass={styles.errorDialog}
          contentClass={styles.errorContent}
        />
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundary(props: { enabled: boolean; children: React.ReactNode }) {
  const history = useHistory();

  if (!props.enabled) {
    return <>{props.children}</>;
  }

  return <InnerErrorBoundary {...props} history={history} />;
}
